import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { X, Send, Phone, MessageSquare } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Emilie's avatar - friendly female support agent
const EMILIE_AVATAR = "https://images.unsplash.com/photo-1765005204058-10418f5123c5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwzfHxmcmllbmRseSUyMGZlbWFsZSUyMGN1c3RvbWVyJTIwc3VwcG9ydCUyMGFnZW50JTIwaGVhZHNob3QlMjBwcm9mZXNzaW9uYWx8ZW58MHx8fHwxNzcxMjc1NTI4fDA&ixlib=rb-4.1.0&q=85&w=100";

export const Chatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: 'bot',
      content: "Hi, I'm Emilie! 👋\n\nI'm here to help you find the perfect IT certification course.\n\n**What can I help you with today?**\n\n• Course recommendations & career paths\n• Pricing & schedules\n• Exam details & prerequisites\n• Booking information\n\nJust ask away! 🚀"
    }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const sendMessage = async () => {
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setLoading(true);

    try {
      const response = await axios.post(`${API}/chat`, {
        message: userMessage
      });

      setMessages(prev => [...prev, {
        role: 'bot',
        content: response.data.response,
        needsHuman: response.data.needs_human
      }]);
    } catch (error) {
      console.error('Chat error:', error);
      setMessages(prev => [...prev, {
        role: 'bot',
        content: "Sorry, I'm having trouble connecting. Please contact us via WhatsApp or phone!",
        needsHuman: true
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <>
      {/* Floating Chat Button */}
      {!isOpen && (
        <button
          data-testid="chatbot-toggle"
          onClick={() => setIsOpen(true)}
          className="fixed bottom-6 right-6 z-50 bg-cyan-500 hover:bg-cyan-400 rounded-full p-3 shadow-2xl transition-all hover:scale-110 group flex items-center gap-2"
        >
          <img 
            src={EMILIE_AVATAR} 
            alt="Emilie" 
            className="w-10 h-10 rounded-full object-cover border-2 border-white"
          />
          <span className="hidden sm:block text-slate-900 font-semibold pr-2">Chat with Emilie</span>
          <span className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full animate-pulse border-2 border-white"></span>
        </button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div 
          data-testid="chatbot-window"
          className="fixed bottom-4 right-4 md:bottom-6 md:right-6 z-50 w-[calc(100vw-2rem)] max-w-md md:w-96 h-[80vh] md:h-[600px] bg-[#0f1420] border border-cyan-500/30 rounded-2xl shadow-2xl flex flex-col overflow-hidden"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-cyan-500 to-blue-500 p-3 md:p-4 flex items-center justify-between">
            <div className="flex items-center gap-2 md:gap-3">
              <div className="relative">
                <img 
                  src={EMILIE_AVATAR} 
                  alt="Emilie" 
                  className="w-10 h-10 md:w-12 md:h-12 rounded-full object-cover border-2 border-white"
                />
                <span className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 rounded-full border-2 border-white"></span>
              </div>
              <div>
                <h3 className="font-bold text-white text-sm md:text-base">Emilie</h3>
                <p className="text-[10px] md:text-xs text-white/80">Course Advisor • Online</p>
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="text-white hover:bg-white/20 rounded-full p-1.5 transition-colors"
              aria-label="Close chat"
            >
              <X className="w-4 h-4 md:w-5 md:h-5" />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-3 md:p-4 space-y-3 md:space-y-4" style={{ scrollBehavior: 'smooth' }}>
            {messages.map((msg, idx) => (
              <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                {msg.role === 'bot' && (
                  <img 
                    src={EMILIE_AVATAR} 
                    alt="Emilie" 
                    className="w-7 h-7 md:w-8 md:h-8 rounded-full object-cover mr-2 flex-shrink-0 mt-1"
                  />
                )}
                <div
                  className={`max-w-[80%] md:max-w-[75%] p-2.5 md:p-3 rounded-2xl text-sm md:text-base ${
                    msg.role === 'user'
                      ? 'bg-cyan-500 text-slate-900 rounded-br-md'
                      : 'bg-slate-800/80 text-slate-200 border border-slate-700 rounded-bl-md'
                  }`}
                >
                  <p className="whitespace-pre-wrap leading-relaxed">{msg.content}</p>
                  {msg.needsHuman && (
                    <div className="mt-3 pt-3 border-t border-slate-600 space-y-2">
                      <p className="text-xs text-cyan-400 font-semibold">Need more help?</p>
                      <div className="flex flex-wrap gap-2">
                        <a
                          href="https://wa.me/447435177049"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 text-[11px] md:text-xs bg-green-500 text-white px-2.5 md:px-3 py-1.5 rounded-full hover:bg-green-600 transition-colors"
                        >
                          <MessageSquare className="w-3 h-3" />
                          WhatsApp
                        </a>
                        <a
                          href="tel:02088088008"
                          className="flex items-center gap-1 text-[11px] md:text-xs bg-purple-500 text-white px-2.5 md:px-3 py-1.5 rounded-full hover:bg-purple-600 transition-colors"
                        >
                          <Phone className="w-3 h-3" />
                          Call Us
                        </a>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <img 
                  src={EMILIE_AVATAR} 
                  alt="Emilie" 
                  className="w-7 h-7 md:w-8 md:h-8 rounded-full object-cover mr-2 flex-shrink-0 mt-1"
                />
                <div className="bg-slate-800/80 border border-slate-700 p-3 rounded-2xl rounded-bl-md">
                  <div className="flex gap-1">
                    <div className="w-2 h-2 bg-cyan-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                    <div className="w-2 h-2 bg-cyan-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                    <div className="w-2 h-2 bg-cyan-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="p-3 md:p-4 border-t border-slate-700 bg-slate-900/50">
            <div className="flex gap-2">
              <Input
                data-testid="chatbot-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Type your message..."
                className="flex-1 bg-slate-800 border-slate-600 text-white placeholder:text-slate-500 text-sm md:text-base focus:border-cyan-500"
                disabled={loading}
              />
              <Button
                data-testid="chatbot-send"
                onClick={sendMessage}
                disabled={loading || !input.trim()}
                className="bg-cyan-500 text-slate-900 hover:bg-cyan-400 px-3 md:px-4"
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
